import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import CreatorCard from "../components/CreatorCard";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, SlidersHorizontal, RotateCcw } from "lucide-react";
import SEO from "../components/SEO";

const NICHES = ["Fashion", "Beauty", "Fitness", "Food", "Travel", "Tech", "Gaming", "Lifestyle", "Startups", "Product Reviews", "AI"];
const PLATFORMS = ["instagram", "tiktok", "youtube", "twitter"];
const TIERS = ["bronze", "silver", "gold", "platinum", "elite"];

export default function Discover() {
  const { user } = useAuth();
  const [creators, setCreators] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    api.get("/admin/me")
      .then((r) => setIsAdmin(!!r.data?.is_admin))
      .catch(() => setIsAdmin(false));
  }, [user]);

  const handleSave = async (creator_id) => {
    try {
      await api.post("/brand/saved-creators", { creator_id, collection: "Favourites" });
      toast.success("Saved to Favourites");
    } catch (e) { toast.error("Save failed"); }
  };

  const handleSuspend = async (creator) => {
    if (!window.confirm(`Suspend ${creator.name}? They won't be able to log in and will be hidden from Discover until reinstated.`)) return;
    try {
      await api.post(`/admin/users/${creator.user_id}/suspend`);
      toast.success(`${creator.name} suspended`);
      setCreators((cs) => cs.map((c) => (c.user_id === creator.user_id ? { ...c, is_suspended: true } : c)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Suspend failed"); }
  };

  const handleUnsuspend = async (creator) => {
    try {
      await api.post(`/admin/users/${creator.user_id}/unsuspend`);
      toast.success(`${creator.name} reinstated`);
      setCreators((cs) => cs.map((c) => (c.user_id === creator.user_id ? { ...c, is_suspended: false } : c)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Reinstate failed"); }
  };

  const handleDelete = async (creator) => {
    if (!window.confirm(`Permanently delete ${creator.name}? This removes their profile, portfolio and account. Hires and reviews stay for audit. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${creator.user_id}`);
      toast.success(`${creator.name} deleted`);
      setCreators((cs) => cs.filter((c) => c.user_id !== creator.user_id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [niche, setNiche] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [minFollowers, setMinFollowers] = useState(0);
  const [minRating, setMinRating] = useState(0);
  const [tier, setTier] = useState("all");
  const [minBrandReady, setMinBrandReady] = useState(0);
  const [minJobs, setMinJobs] = useState(0);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [location, setLocation] = useState("");
  const [sortBy, setSortBy] = useState("recommended");
  const [advOpen, setAdvOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (niche !== "all") params.niche = niche;
      if (platform !== "all") params.platform = platform;
      if (minFollowers > 0) params.min_followers = minFollowers;
      if (minRating > 0) params.min_rating = minRating;
      if (tier !== "all") params.badge_tier = tier;
      if (minBrandReady > 0) params.min_brand_ready = minBrandReady;
      if (minJobs > 0) params.min_completed_jobs = minJobs;
      if (verifiedOnly) params.verified_only = true;
      if (location.trim()) params.location = location.trim();
      if (sortBy && sortBy !== "recommended") params.sort_by = sortBy;
      if (isAdmin) params.include_suspended = true;
      const { data } = await api.get("/creators", { params });
      setCreators(data);
      setLoadError(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load creators";
      setLoadError(typeof msg === "string" ? msg : "Failed to load creators");
      toast.error("Failed to load creators — tap Retry");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [isAdmin]);

  const reset = () => {
    setSearch(""); setNiche("all"); setPlatform("all"); setMinFollowers(0); setMinRating(0); setTier("all");
    setMinBrandReady(0); setMinJobs(0); setVerifiedOnly(false); setLocation(""); setSortBy("recommended");
    setTimeout(load, 0);
  };

  return (
    <div className="min-h-screen grain">
      <SEO
        title="Discover Verified Creators"
        description="Browse verified TikTok, Instagram and YouTube creators on Nexus. Filter by niche, platform, follower count, star rating and badge tier. Every hire is escrow protected."
        path="/discover"
        keywords={[
          "find influencers",
          "browse creators",
          "influencer search",
          "niche creators",
          "fashion creators",
          "beauty creators",
          "fitness creators",
          "travel creators",
          "food creators",
        ]}
      />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Discover</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
              Find your next <span className="nexus-gradient-text">creator collab</span>.
            </h1>
            <p className="text-zinc-400 mt-2">
              Every profile is verified and escrow-protected.
            </p>
          </div>
          <div className="text-sm text-zinc-500" data-testid="discover-result-count">
            {loading ? "Loading…" : `${creators.length} creator${creators.length === 1 ? "" : "s"} available`}
          </div>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Filters */}
          <aside className="space-y-6" data-testid="discover-filters">
            <div className="glass rounded-2xl p-5 space-y-5 sticky top-24">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400">
                  <SlidersHorizontal className="h-4 w-4" /> Filters
                </div>
                <button onClick={reset} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1" data-testid="filter-reset-btn">
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Search</label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="name, niche…"
                    data-testid="filter-search-input"
                    className="pl-9 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Niche</label>
                <Select value={niche} onValueChange={setNiche}>
                  <SelectTrigger data-testid="filter-niche-select" className="bg-white/[0.02] border-white/10 text-white">
                    <SelectValue placeholder="All niches" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
                    <SelectItem value="all">All niches</SelectItem>
                    {NICHES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Platform</label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger data-testid="filter-platform-select" className="bg-white/[0.02] border-white/10 text-white">
                    <SelectValue placeholder="All platforms" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
                    <SelectItem value="all">All platforms</SelectItem>
                    {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex justify-between text-xs uppercase tracking-widest text-zinc-500 mb-3">
                  <span>Min followers</span><span className="text-white">{minFollowers.toLocaleString()}</span>
                </div>
                <Slider
                  value={[minFollowers]}
                  onValueChange={(v) => setMinFollowers(v[0])}
                  min={0}
                  max={500000}
                  step={5000}
                  data-testid="filter-followers-slider"
                />
              </div>

              <div>
                <div className="flex justify-between text-xs uppercase tracking-widest text-zinc-500 mb-3">
                  <span>Min rating</span><span className="text-white">{minRating.toFixed(1)}</span>
                </div>
                <Slider
                  value={[minRating]}
                  onValueChange={(v) => setMinRating(v[0])}
                  min={0}
                  max={5}
                  step={0.5}
                  data-testid="filter-rating-slider"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Badge tier</label>
                <div className="grid grid-cols-2 gap-2">
                  {[{ v: "all", l: "All" }, ...TIERS.map((t) => ({ v: t, l: t.charAt(0).toUpperCase() + t.slice(1) }))].map(({ v, l }) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setTier(v)}
                      data-testid={`filter-tier-${v}`}
                      className={`px-2.5 py-1.5 text-xs rounded-full border transition-colors ${
                        tier === v
                          ? "border-nexus-purple bg-nexus-purple/10 text-white"
                          : "border-white/10 text-zinc-400 hover:border-white/25"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Sort by</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="filter-sort-select" className="bg-white/[0.02] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
                    <SelectItem value="recommended">Recommended</SelectItem>
                    <SelectItem value="brand_ready">Brand-ready score</SelectItem>
                    <SelectItem value="followers">Most followers</SelectItem>
                    <SelectItem value="rating">Highest rated</SelectItem>
                    <SelectItem value="jobs">Most completed jobs</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Advanced */}
              <div>
                <button
                  type="button"
                  onClick={() => setAdvOpen((v) => !v)}
                  className="w-full flex items-center justify-between text-xs uppercase tracking-widest text-zinc-400 hover:text-white py-2"
                  data-testid="filter-advanced-toggle"
                >
                  <span>Advanced</span>
                  <span className="text-[10px]">{advOpen ? "− Hide" : "+ Show"}</span>
                </button>
                {advOpen && (
                  <div className="space-y-5 pt-2" data-testid="filter-advanced-panel">
                    <div>
                      <div className="flex justify-between text-xs uppercase tracking-widest text-zinc-500 mb-3">
                        <span>Min brand-ready</span><span className="text-white">{minBrandReady}</span>
                      </div>
                      <Slider value={[minBrandReady]} onValueChange={(v) => setMinBrandReady(v[0])} min={0} max={100} step={5} data-testid="filter-brand-ready-slider" />
                    </div>
                    <div>
                      <div className="flex justify-between text-xs uppercase tracking-widest text-zinc-500 mb-3">
                        <span>Min completed jobs</span><span className="text-white">{minJobs}</span>
                      </div>
                      <Slider value={[minJobs]} onValueChange={(v) => setMinJobs(v[0])} min={0} max={50} step={1} data-testid="filter-min-jobs-slider" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Location</label>
                      <Input
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="e.g. London, UK"
                        data-testid="filter-location-input"
                        className="bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="verified-only" checked={verifiedOnly} onCheckedChange={(v) => setVerifiedOnly(!!v)} data-testid="filter-verified-only" />
                      <label htmlFor="verified-only" className="text-xs text-zinc-300 cursor-pointer select-none">Verified creators only</label>
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={load}
                className="nexus-gradient rounded-full text-white font-semibold w-full border-0 hover:opacity-90"
                data-testid="filter-apply-btn"
              >
                Apply filters
              </Button>
            </div>
          </aside>

          {/* Grid */}
          <div>
            {loading ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="rounded-2xl hairline bg-[#0C0C10] h-80 animate-pulse" />
                ))}
              </div>
            ) : loadError ? (
              <div className="glass rounded-2xl p-10 text-center" data-testid="discover-error">
                <div className="text-xs uppercase tracking-widest text-nexus-pink mb-3">Couldn&apos;t reach the creators</div>
                <div className="font-display text-2xl mb-2">We hit a snag loading Discover</div>
                <p className="text-zinc-400 text-sm mb-5 break-words max-w-xl mx-auto">{loadError}</p>
                <button
                  onClick={() => { setLoading(true); setLoadError(null); load(); }}
                  className="nexus-gradient text-white rounded-full px-6 h-10 text-sm font-semibold hover:opacity-90"
                  data-testid="discover-retry-btn"
                >
                  Retry
                </button>
              </div>
            ) : creators.length === 0 ? (
              <div className="glass rounded-2xl p-12 text-center" data-testid="discover-empty">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Nothing here yet</div>
                <div className="font-display text-2xl mb-2">No creators match those filters</div>
                <p className="text-zinc-400">Try loosening the follower or rating minimums.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="discover-grid">
                {creators.map((c, i) => (
                  <CreatorCard
                    key={c.user_id}
                    creator={{
                      ...c,
                      __onSave: user?.role === "brand" ? handleSave : undefined,
                      __admin: isAdmin ? { onSuspend: handleSuspend, onUnsuspend: handleUnsuspend, onDelete: handleDelete } : undefined,
                    }}
                    index={i}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
